/**
 * Condition Node Component
 *
 * Represents a branching decision point in the story flow based on an expression.
 * Displays as a diamond shape with a condition script that evaluates to a boolean.
 * Has one input handle and two output handles (true branch / false branch).
 *
 * The condition expression is evaluated during play mode to determine which
 * outgoing edge is taken. Invalid expressions are marked with an error badge.
 *
 * @module model/nodeTypes/condition
 */

import React, { useCallback, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { parseScript } from '../../scripting/parser';
import type { Node as FlowNode } from '@xyflow/react';

/**
 * Condition Node Data Type
 *
 * Defines the structure of data stored in a Condition node.
 * The `expression` is the script that will be evaluated.
 */
export type ConditionNodeData = {
  expression: string;
  name?: string;
};

/**
 * Condition Node Metadata
 *
 * Registry entry for the Condition node type.
 * Includes display information, schema, and default values.
 */
export const conditionNodeMeta = {
  type: 'condition' as const,
  label: 'Condition',
  icon: '◇',
  description: 'Branch based on a boolean expression',
  category: 'logic',
  defaultData: {
    expression: '',
    name: 'Condition',
  } as ConditionNodeData,
  handles: {
    inputs: [{ id: 'in', label: 'In', position: Position.Top }],
    outputs: [
      { id: 'true', label: 'True', position: Position.Bottom, style: { left: '33%' } },
      { id: 'false', label: 'False', position: Position.Bottom, style: { left: '66%' } },
    ],
  },
};

/**
 * Parse and validate the condition expression
 *
 * Returns one of three states:
 * - `valid: true` — expression parses successfully
 * - `valid: false, isEmpty: true` — nothing typed yet (a brand new node's
 *   default state; not an error, just unconfigured)
 * - `valid: false, isEmpty: false` — non-empty but fails to parse, a real error
 *
 * Distinguishing "empty" from "broken" matters for the UI: a freshly
 * dropped node has an empty expression by default, and showing a red error
 * badge on every single node the moment it's created (before the user has
 * had any chance to type something) reads as "this is broken" rather than
 * "please configure me".
 *
 * @param expression The condition script to validate
 * @returns Validation result
 */
function validateCondition(expression: string): {
  valid: boolean;
  isEmpty: boolean;
  error?: string;
} {
  if (!expression || !expression.trim()) {
    return { valid: false, isEmpty: true };
  }

  const parseResult = parseScript(expression);
  if (!parseResult.success) {
    return {
      valid: false,
      isEmpty: false,
      error: parseResult.error || 'Parse error',
    };
  }

  return { valid: true, isEmpty: false };
}

/**
 * Condition Node Component
 *
 * Renders a diamond-shaped node with:
 * - Expression input field
 * - Validation badge (checkmark or error icon)
 * - Input handle (top) and two output handles (bottom: true/false)
 * - Double-click to edit in expanded mode
 *
 * The node's visual appearance adjusts based on:
 * - Validation state (border color changes on error)
 * - Selection state (React Flow's default selection styling)
 * - Semantic zoom level (can show/hide details at different zoom levels)
 */
export const ConditionNode = React.memo((props: any) => {
  const { data, selected, isConnectable } = props;
  const { setNodes } = useReactFlow();
  const [isEditing, setIsEditing] = React.useState(false);
  const [tempExpression, setTempExpression] = React.useState(data.expression);

  const validation = useMemo(() => validateCondition(data.expression), [data.expression]);

  /**
   * Handle expression changes
   *
   * Updates the node data in the store. The parent component handles
   * propagating changes to the server.
   */
  const handleExpressionChange = useCallback(
    (expr: string) => {
      setNodes((nodes: FlowNode[]) =>
        nodes.map((n) =>
          n.id === props.id
            ? { ...n, data: { ...n.data, expression: expr } }
            : n
        )
      );
    },
    [props.id, setNodes]
  );

  const handleSaveEdit = () => {
    handleExpressionChange(tempExpression);
    setIsEditing(false);
  };

  if (isEditing) {
    // Expanded editing mode
    return (
      <div
        className="nopan nodrag border border-blue-500 rounded-lg bg-white dark:bg-slate-800 p-4 w-80 shadow-lg z-50"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3">
          <label className="block text-sm font-semibold mb-2">Condition Expression</label>
          <textarea
            value={tempExpression}
            onChange={(e) => setTempExpression(e.target.value)}
            className="w-full p-2 border rounded font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={4}
            placeholder="e.g., quest.stage >= 2 && inventory.has_key"
            autoFocus
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleSaveEdit}
            className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded font-medium"
          >
            Save
          </button>
          <button
            onClick={() => {
              setTempExpression(data.expression);
              setIsEditing(false);
            }}
            className="flex-1 px-3 py-2 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-700 text-sm rounded font-medium"
          >
            Cancel
          </button>
        </div>

        {!validation.valid && !validation.isEmpty && (
          <div className="mt-3 p-2 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded text-sm text-red-700 dark:text-red-200">
            {validation.error}
          </div>
        )}
      </div>
    );
  }

  // Compact display mode
  return (
    <div
      className={`
        nopan border-2 rounded-lg p-3 bg-white dark:bg-slate-800 cursor-pointer
        transition-colors duration-200
        ${
          validation.valid
            ? 'border-green-400 dark:border-green-600'
            : validation.isEmpty
            ? 'border-gray-300 dark:border-gray-600'
            : 'border-red-400 dark:border-red-600'
        }
        ${selected ? 'ring-2 ring-blue-400' : ''}
      `}
      style={{
        width: '140px',
        aspectRatio: '1 / 1',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onDoubleClick={() => setIsEditing(true)}
    >
      {/* Top input handle */}
      <Handle
        type="target"
        position={Position.Top}
        id="in"
        isConnectable={isConnectable}
        className="w-3 h-3"
      />

      {/* Main content area */}
      <div className="text-center flex-1 flex flex-col items-center justify-center gap-2">
        <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">
          {data.name || 'Condition'}
        </div>

        {/* Expression preview (truncated) */}
        {data.expression && (
          <div title={data.expression} className="text-xs font-mono text-gray-600 dark:text-gray-400 truncate max-w-[90%]">
            {data.expression.substring(0, 15)}
            {data.expression.length > 15 ? '...' : ''}
          </div>
        )}

        {/* Validation badge */}
        <div
          className={`text-xs font-medium px-2 py-1 rounded ${
            validation.valid
              ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200'
              : validation.isEmpty
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300'
              : 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200'
          }`}
          title={validation.isEmpty ? 'Double-click to set a condition' : validation.error}
        >
          {validation.valid ? '✓ Valid' : validation.isEmpty ? 'Not set' : '✗ Error'}
        </div>
      </div>

      {/* Bottom output handles (true and false branches) */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="true"
        isConnectable={isConnectable}
        className="w-3 h-3"
        style={{ left: 'calc(25% + 5px)' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="false"
        isConnectable={isConnectable}
        className="w-3 h-3"
        style={{ left: 'calc(75% - 5px)' }}
      />

    </div>
  );
});

ConditionNode.displayName = 'ConditionNode';
